import { useList, useDelete, useUpdate, useInvalidate } from "@refinedev/core";

import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bot, Plus, Trash2, Pencil, Webhook, Radio, Info, Eye, PowerOff } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { getActiveStatus } from "@/components/shared/status-badge";
import { DataTable, TableToolbar, type Column, type BulkAction, type SortState } from "@/components/tables";
import { useConfirm } from "@/hooks/use-confirm";
import { FormHeader } from "@/components/forms";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import type { TelegramBot } from "./types";
import { BotTokensForm } from "./form";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";

// BotTokensList component with correct data handling
/**
 * BotTokensList
 * 
 * Список bot-tokens с фильтрацией и управлением
 * 
 * @returns {React.ReactElement} React компонент
 */
export function BotTokensList() {
  useScrollRestoration();
  const [deleteBot, setDeleteBot] = useState<string | null>(null);
  const [selectedBot, setSelectedBot] = useState<TelegramBot | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = usePersistedState("bot-tokens:search", "");
  const [page, setPage] = usePersistedState("bot-tokens:page", 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>("bot-tokens:sort", { field: 'name', order: 'asc' });
  const [activeFilter, setActiveFilter] = usePersistedState("bot-tokens:activeFilter", 'all');
  const pageSize = 20;
  const invalidate = useInvalidate();
  const { confirm, ConfirmationDialog } = useConfirm();

  // Fetch bots using standard Refine useList hook
  const { result, query } = useList<TelegramBot>({
    resource: "bot-tokens",
    pagination: { currentPage: page, pageSize, mode: "server" },
    filters: (() => {
      const f: { field: string; operator: 'contains' | 'eq'; value: string | boolean }[] = [];
      if (search) { f.push({ field: 'search', operator: 'contains', value: search }); }
      if (activeFilter !== 'all') { f.push({ field: 'isActive', operator: 'eq', value: activeFilter === 'true' }); }
      return f;
    })(),
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const bots = result.data ?? [];
  const total = result.total ?? 0;
  const isLoading = query.isLoading;

  // Delete mutation
  const { mutate: deleteMutation, mutation } = useDelete();
  const isDeleting = mutation.isPending;

  const { mutate: updateMutation } = useUpdate<TelegramBot>();

  const handleDelete = () => {
    if (deleteBot) {
      deleteMutation(
        {
          resource: "bot-tokens",
          id: deleteBot,
        },
        {
          onSuccess: () => {
            setDeleteBot(null);
            // Invalidate list to refresh data
            invalidate({
              resource: "bot-tokens",
              invalidates: ["list"],
            });
          },
        }
      );
    }
  };

  const handleCreate = () => {
    setSelectedBot(null);
    setIsModalOpen(true);
  };

  const handleEdit = (bot: TelegramBot) => {
    setSelectedBot(bot);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedBot(null);
  };

  const handleSuccess = () => {
    handleModalClose();
    invalidate({
      resource: "bot-tokens",
      invalidates: ["list"],
    });
  };

  const handleRevoke = async (items: TelegramBot[]) => {
    if (items.length === 0) { return; }
    const confirmed = await confirm({
      title: items.length === 1
        ? `Деактивировать бота «${items[0].name}»?`
        : `Деактивировать ${items.length} ботов?`,
      description: 'Бот будет отключён. Вы сможете повторно активировать его через редактирование.',
      variant: 'destructive',
      confirmText: 'Деактивировать',
      cancelText: 'Отмена',
    });
    if (!confirmed) { return; }
    for (const bot of items) {
      updateMutation(
        { resource: 'bot-tokens', id: bot.id, values: { isActive: false } },
        {
          onSuccess: () => {
            invalidate({ resource: 'bot-tokens', invalidates: ['list'] });
          },
        },
      );
    }
  };

  const columns: Column<TelegramBot>[] = [
    {
      key: "name",
      header: "Бот",
      sortable: true,
      render: (value, row) => (
        <HoverCard>
          <HoverCardTrigger asChild>
            <div className="flex items-center gap-3 cursor-pointer">
              {row.avatarUrl ? (
                <img
                  src={row.avatarUrl}
                  alt={row.name}
                  className="h-10 w-10 rounded-full object-cover border"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-primary" />
                </div>
              )}
              <div className="flex flex-col">
                <span className="font-medium flex items-center gap-2">
                  {String(value)}
                  {row.description && (
                    <Info className="h-3 w-3 text-muted-foreground opacity-50" />
                  )}
                </span>
                {row.username && (
                  <a
                    href={`https://t.me/${row.username}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-muted-foreground hover:text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    @{row.username}
                  </a>
                )}
              </div>
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-80">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {row.avatarUrl && (
                  <img
                    src={row.avatarUrl}
                    alt={row.name}
                    className="h-12 w-12 rounded-full border"
                  />
                )}
                <div>
                  <h4 className="text-sm font-semibold">{row.name}</h4>
                  <p className="text-xs text-muted-foreground">
                    @{row.username}
                  </p>
                </div>
              </div>
              {row.shortDescription && (
                <p className="text-sm italic text-muted-foreground border-l-2 pl-2">
                  {row.shortDescription}
                </p>
              )}
              {row.description && <p className="text-sm">{row.description}</p>}
              {row.capabilities && (
                <div className="pt-2 border-t space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">
                    Возможности:
                  </p>
                  <div className="grid grid-cols-1 gap-1 text-xs">
                    <span
                      className={
                        row.capabilities.canJoinGroups
                          ? "text-green-600"
                          : "text-gray-400"
                      }
                    >
                      • Группы: {row.capabilities.canJoinGroups ? "Да" : "Нет"}
                    </span>
                    <span
                      className={
                        row.capabilities.canReadAllGroupMessages
                          ? "text-green-600"
                          : "text-gray-400"
                      }
                    >
                      • Чтение сообщений:{" "}
                      {row.capabilities.canReadAllGroupMessages ? "Да" : "Нет"}
                    </span>
                    <span
                      className={
                        row.capabilities.supportsInlineQueries
                          ? "text-green-600"
                          : "text-gray-400"
                      }
                    >
                      • Inline режим:{" "}
                      {row.capabilities.supportsInlineQueries ? "Да" : "Нет"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </HoverCardContent>
        </HoverCard>
      ),
    },
    {
      key: "token",
      header: "Токен",
      render: (value) => {
        const token = String(value);
        const masked = token.length > 10 ? `${token.slice(0, 10)}***` : '***';
        return (
          <code className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
            {masked}
          </code>
        );
      },
    },
    {
      key: "webhookEnabled",
      header: "Режим",
      width: "120px",
      render: (value) => (
        <div className="flex items-center gap-2">
          {value ? (
            <>
              <Webhook className="h-4 w-4 text-green-600" />
              <span className="text-xs text-green-600 font-medium">Webhook</span>
            </>
          ) : (
            <>
              <Radio className="h-4 w-4 text-blue-500" />
              <span className="text-xs text-blue-500 font-medium">Polling</span>
            </>
          )}
        </div>
      ),
    },
    {
      key: "isActive",
      header: "Статус",
      sortable: true,
      width: "120px",
      render: (value) => (
        <StatusBadge
          status={getActiveStatus(Boolean(value))}
          showIcon={false}
          customLabel={value ? "Активен" : "Отключен"}
        />
      ),
    },
    {
      key: "statistics",
      header: "Статистика",
      render: (_, row) => {
        if (!row.statistics) {return <span className="text-muted-foreground">—</span>;}
        return (
          <div className="flex flex-col text-xs space-y-1">
            <span className="text-muted-foreground">
              Пользователей:{" "}
              <span className="font-medium text-foreground">
                {row.statistics.totalUsers ?? 0}
              </span>
            </span>
            <span className="text-muted-foreground">
              Активных:{" "}
              <span className="font-medium text-foreground">
                {row.statistics.activeSubscriptions ?? 0}
              </span>
            </span>
          </div>
        );
      },
    },

    {
      key: "adminChannels",
      header: "Админ в каналах",
      render: (_, row) => {
        const channels = Array.isArray(row.adminChannels) ? row.adminChannels : [];
        if (channels.length === 0)
          {return <span className="text-muted-foreground text-sm">—</span>;}

        return (
          <HoverCard>
            <HoverCardTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs font-normal"
              >
                {channels.length}{" "}
                {channels.length === 1
                  ? "канал"
                  : (channels.length < 5
                  ? "канала"
                  : "каналов")}
              </Button>
            </HoverCardTrigger>
            <HoverCardContent align="end" className="w-64 p-2">
              <div className="flex flex-col gap-1">
                <p className="text-xs font-semibold text-muted-foreground px-2 py-1">
                  Каналы с правами админа:
                </p>
                {channels.map((ch) => (
                  <div
                    key={ch.id}
                    className="flex items-center gap-2 p-1.5 hover:bg-muted rounded-md transition-colors"
                    title={ch.title}
                  >
                    {ch.photoUrl ? (
                      <img
                        src={ch.photoUrl}
                        alt={ch.title}
                        className="h-6 w-6 rounded-full flex-shrink-0 bg-secondary"
                      />
                    ) : (
                      <div className="h-6 w-6 rounded-full bg-secondary flex-shrink-0" />
                    )}
                    <div className="flex flex-col overflow-hidden">
                      <span className="text-xs font-medium truncate">
                        {ch.title}
                      </span>
                      {ch.purchasedAccessCount !== undefined && (
                        <span className="text-[10px] text-muted-foreground">
                          {ch.purchasedAccessCount} с доступом
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      },
    },
  ];

  const bulkActions: BulkAction<TelegramBot>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) { handleEdit(items[0]); }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Редактировать",
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          handleEdit(items[0]);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Деактивировать',
      icon: <PowerOff className="h-4 w-4" />,
      onClick: (items) => { void handleRevoke(items); },
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          setDeleteBot(items[0].id);
        }
      },
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Токены бота"
        description="Управление Telegram ботами"
        actions={
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Добавить бота
          </Button>
        }
        sticky
      />

      <div className="flex items-center gap-3">
        <TableToolbar
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1); }}
          searchPlaceholder="Поиск по имени бота..."
        />
        <Select value={activeFilter} onValueChange={(value) => { setActiveFilter(value); setPage(1); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Все" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все</SelectItem>
            <SelectItem value="true">Активные</SelectItem>
            <SelectItem value="false">Неактивные</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable<TelegramBot>
        columns={columns}
        data={bots}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={(p) => setPage(p)}
        onRowClick={(row) => handleEdit(row as TelegramBot)}
        emptyMessage="Нет ботов"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={(newSort) => { setSort(newSort); setPage(1); }}
        bulkActions={bulkActions}
      />

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {selectedBot ? "Редактировать бота" : "Новый бот"}
            </DialogTitle>
            <DialogDescription>
              {selectedBot
                ? `Редактирование бота "${selectedBot.name}"`
                : "Добавление нового Telegram бота"}
            </DialogDescription>
          </DialogHeader>
          <BotTokensForm
            initialData={selectedBot}
            onSuccess={handleSuccess}
            onCancel={handleModalClose}
          />
        </DialogContent>
      </Dialog>

      {ConfirmationDialog}

      <AlertDialog
        open={!!deleteBot}
        onOpenChange={(open) => !open && setDeleteBot(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить бота?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Бот будет остановлен и его токен
              будет удалён.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
